/**
 * TrendChartsContainer Component
 * 
 * Container component that manages trend chart data and layout
 * with date range integration and responsive design.
 */

import React, { useEffect, useState } from 'react';
import Column from '@amzn/meridian/column';
import Divider from "@amzn/meridian/divider"
import Row from '@amzn/meridian/row';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import Alert from '@amzn/meridian/alert';
import Responsive from '@amzn/meridian/responsive';
import { 
  TechnologyChart,
  MonitronChart,
  CadetChart,
  ThermalChart 
} from './technology-chart';
import { UltrasoundSection } from '../ultrasound-section';
import { OilSection } from '../oil-section';
import { useAllTrendChartData } from '../../../hooks/use-trend-chart-data';
import { TechnologyType } from '../../../services/equipment/types';

export interface TrendChartsContainerProps {
  site?: string;
  /** The ID of the equipment to show charts for */
  equipmentId?: string;
  position?: string;
  /** Whether to use date range filtering from context */
  useDateRangeFiltering?: boolean;
  /** Custom title for the charts section */
  title?: string;
  /** Technologies to display (defaults to all) */
  technologies?: TechnologyType[];
  /** Whether to show charts in a grid layout */
  useGridLayout?: boolean;
  /** Available technologies from equipment node (for conditional rendering) */
  availableTechnologies?: string[];
}

/**
 * Empty state component for when no equipment is selected
 */
const ChartsEmptyState: React.FC<{ title: string }> = ({ title }) => (
  <Column alignmentHorizontal="start" spacing="300">
    <Column alignmentHorizontal="start" spacing="200">
      <Heading level={4}>{title}</Heading>
      <Alert>
        Select an equipment node from the tree to view CBM Signals and Models.
      </Alert>
    </Column>
  </Column>
);

/**
 * Error state component for when chart data fails to load
 */
const ChartsErrorState: React.FC<{ title: string; error: string }> = ({ title, error }) => (
  <Column spacing="300">
    <Heading level={4}>{title}</Heading>
    <Alert type="error">
      Failed to load CBM Signal: {error}
    </Alert>
  </Column>
);

/**
 * Technology mapping for conditional rendering
 */
const TECHNOLOGY_MAPPING = {
  'M': 'monitron',
  'U': 'ultrasound', 
  'C': 'cadet',
  'T': 'thermal',
  'O': 'oil'
} as const;

/**
 * Grid layout component for charts
 */
const ChartsGrid: React.FC<{
  technologies: TechnologyType[];
  chartData: any;
  containerWidth: number;
  site?: string;
  equipmentId?: string;
  position?: string;
  availableTechnologies?: string[];
}> = ({ technologies, chartData, containerWidth, site, equipmentId, position, availableTechnologies }) => {
  
  // Helper to check if technology should be shown
  const shouldShowTechnology = (techCode: string): boolean => {
    return !availableTechnologies || availableTechnologies.includes(techCode);
  };
  // Calculate chart width based on container and number of columns
  const getChartWidth = (isLargeDesktop: boolean, isDesktop: boolean, isTablet: boolean) => {
  if (isLargeDesktop) {
    return Math.floor((containerWidth) / 1.6); // 2 columns on large desktop
  } else if (isDesktop) {
    return containerWidth - 40; // Full width on laptop
  } else if (isTablet) {
    return containerWidth - 32; // 1 column on tablet
  } else {
    return containerWidth - 16; // 1 column on mobile
  }
};


  return (
    <Responsive
  query="min-width"
  props={{
    isLargeDesktop: { default: false, '1440px': true },
    isDesktop: { default: false, '1024px': true },
    isTablet: { default: false, '768px': true },
  }}
>

      {({ isLargeDesktop, isDesktop, isTablet }) => {
      const chartWidth = getChartWidth(isLargeDesktop, isDesktop, isTablet);

        
        // Create array of sections to render conditionally
        const sectionsToRender = [
          {
            condition: shouldShowTechnology('U'),
            component: (
              <UltrasoundSection 
                data={chartData.pulse.data} 
                isLoading={chartData.pulse.isLoading}
                error={chartData.pulse.error}
              />
            )
          },
          {
            condition: shouldShowTechnology('M'),
            component: (
              <MonitronChart 
                data={chartData.monitron.data} 
                isLoading={chartData.monitron.isLoading}
                error={chartData.monitron.error}
                containerWidth={chartWidth}
                site={site}
                equipmentId={equipmentId}
                position={position}
              />
            )
          },
          {
            condition: shouldShowTechnology('C'),
            component: (
              <CadetChart
                data={chartData.cadet.data}
                isLoading={chartData.cadet.isLoading}
                error={chartData.cadet.error}
                containerWidth={chartWidth}
              />
            )
          },
          {
            condition: shouldShowTechnology('T'),
            component: (
              <Row spacing="400">
                <ThermalChart
                  data={chartData.thermal.data}
                  isLoading={chartData.thermal.isLoading}
                  error={chartData.thermal.error}
                  containerWidth={chartWidth}
                  customColors={{
                    temperature: '#fd7e14',  
                    baseline: '#6c757d'
                  }}
                />
              </Row>
            )
          },
          {
            condition: shouldShowTechnology('O'),
            component: (
              <Row spacing="400">
                <OilSection />
              </Row>
            )
          }
        ].filter(section => section.condition);

        // Render sections with proper dividers
        return (
          <Column spacing="400">
            {sectionsToRender.map((section, index) => (
              <React.Fragment key={index}>
                {index > 0 && <Divider />}
                {section.component}
              </React.Fragment>
            ))}
          </Column>
        );
      }}
    </Responsive>
  );
};

/**
 * TrendChartsContainer component that handles data fetching and layout
 */
export const TrendChartsContainer: React.FC<TrendChartsContainerProps> = ({
  site,
  equipmentId,
  position,
  useDateRangeFiltering = true,
  title = 'CBM Signals and Models',
  technologies = ['monitron', 'pulse'],
  useGridLayout = true,
  availableTechnologies,
}) => {
  const [containerWidth, setContainerWidth] = useState(1200);

  const {
    data: chartData,
    isLoading,
    hasError,
    errors,
    clearAll
  } = useAllTrendChartData({
    site,
    equipmentId,
    position,
    autoFetch: true,
    useDateRangeFiltering
  });

  // Clear charts when equipmentId is undefined
  useEffect(() => {
    if (!site || !equipmentId || !position) {
      clearAll();
      return;
    }
  }, [site, equipmentId, position, clearAll]);

  // Handle container width for responsive charts
  useEffect(() => {
    const updateContainerWidth = () => {
      const container = document.querySelector('[data-testid="trend-charts-container"]');
      if (container) {
        setContainerWidth(container.clientWidth);
      } else {
        setContainerWidth(window.innerWidth);
      }
    };

    updateContainerWidth();
    window.addEventListener('resize', updateContainerWidth);
    return () => window.removeEventListener('resize', updateContainerWidth);
  }, []);

  // If no equipment is selected, show empty state
  if (!site || !equipmentId || !position) {
    return <ChartsEmptyState title={title} />;
  }

  // If there's a global error, show error state
  if (hasError && errors) {
    return <ChartsErrorState title={title} error={errors} />;
  }

  return (
    <Column spacing="400" data-testid="trend-charts-container" alignmentHorizontal='center'>
      <Row alignmentHorizontal='start'>
        <Heading level={4}>{title}</Heading>
        {isLoading && (
          <Text>Loading...</Text>
        )}
      </Row>
      <Divider/>


      {useGridLayout ? (
        <ChartsGrid 
          technologies={technologies}
          chartData={chartData}
          containerWidth={containerWidth}
          site={site}
          equipmentId={equipmentId}
          position={position}
          availableTechnologies={availableTechnologies}
        />
      ) : (
        <Column spacing="400">
          {Object.entries(chartData).map(([tech, data]) => (
            <TechnologyChart
              key={tech}
              technology={tech as TechnologyType}
              data={data.data}
              isLoading={data.isLoading}
              error={data.error}
              containerWidth={containerWidth}
            />
          ))}
        </Column>
      )}
    </Column>
  );
};
